import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationStart } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { AuthService } from '../../auth/auth.service';
import { AnalyticsService } from 'app/shared/services/analytics.service';
import { TokensService } from 'app/shared/services/tokens.service';

import { SvgIconComponent } from '../../shared/components/svg-icon/svg-icon.component';
import { AuthFormComponent } from '../../shared/components/auth-form/auth-form.component';
import { ProfileFlyoutComponent } from '../profile-flyout/profile-flyout.component';
import { NavigationBarComponent } from '../navigation-bar/navigation-bar.component';
import { TokenStatusComponent } from '../../shared/components/token-status/token-status.component';

import { ISvgConfig } from '../../shared/interfaces/svg.interfaces';

import { ESvgTypes } from '../../shared/enums/svg.enums';
import { EButtonActionTypes } from 'app/shared/enums/button.enums';
import { EAuthButtonText, EAuthType } from 'app/auth/auth.enums';
import { ELocalStorageKey } from 'app/shared/enums/shared.enums';
import { EProfileState, EProfileTab } from 'app/enums/profile.enums';
import { ETokenType, ETokenUsageCase } from 'app/shared/enums/tokens.enums';

@Component({
  selector: 'stxt-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SvgIconComponent,
    ProfileFlyoutComponent,
    TokenStatusComponent,
    NavigationBarComponent
  ]
})
export class HeaderComponent implements OnInit, OnDestroy {
  public profileSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'profile' };
  public openProfileIntent: EProfileTab = EProfileTab.Account;
  public getTokensScreen: boolean = false;
  public EProfileTab = EProfileTab;
  public $isUser = this.authService.$isUser;
  public ETokenUsageCase = ETokenUsageCase;
  public showProfileFlyout: boolean;
  public showDropdown: boolean = false;
  public user = JSON.parse(localStorage.getItem(ELocalStorageKey.User));
  public userId: string = localStorage.getItem(ELocalStorageKey.UserId);
  public pageUrl: string;
  public tokenType: ETokenType = ETokenType.HasFunds;
  public ETokenType = ETokenType;
  public tokensBalance: number;

  private tokensBalanceEvent$: Subscription;
  private tokensBalanceSubscription: Subscription;
  private routerSub?: Subscription;

  protected readonly EAuthButtonText = EAuthButtonText;
  protected readonly EAuthType = EAuthType;

  constructor(
    public modal: NgbModal,
    public router: Router,
    public authService: AuthService,
    public analyticsService: AnalyticsService,
    private readonly tokensService: TokensService
  ) {}

  ngOnInit(): void {
    this.isUser();

    this.subscribeToTokensBalance();
    this.tokensService.fetchTokensBalance();
    this.trackTokenBalanceEvent();
  }

  trackTokenBalanceEvent(): void {
    this.tokensBalanceEvent$ = this.tokensService.getMoreTokensEvent$.subscribe((res: boolean) => {
      this.showProfileFlyout = res;
      this.showDropdown = res;
      this.getTokensScreen = res;
      this.showProfileFlyout ? this.startRouterListener() : this.stopRouterListener();

      if (!res) return;

      this.openProfileIntent = EProfileTab.Wallet;
    });
  }

  subscribeToTokensBalance(): void {
    this.tokensBalanceSubscription = this.tokensService.tokensBalance$.subscribe(balance => {
      this.tokensBalance = balance;
    });
  }

  async isUser(): Promise<void> {
    await this.authService.currentSession();
  }

  openAuth(authType: string, clickText: string): void {
    let modalRef;
    if (window.innerWidth < 640) {
      modalRef = this.modal.open(AuthFormComponent, {
        size: 'sm',
        windowClass: 'modal-fullscreen'
      });
    } else {
      modalRef = this.modal.open(AuthFormComponent);
    }
    modalRef.componentInstance.isSignUp = authType === EAuthType.SignUp;
    let authButtonType;
    const pageName = this.getPageName(this.pageUrl);
    authType === EAuthType.Login
      ? (authButtonType = EButtonActionTypes.TriggerLogin)
      : (authButtonType = EButtonActionTypes.TriggerSignUp);
    this.analyticsService.click(clickText, '', pageName, authButtonType, '');
  }

  getPageName(url: string): string {
    if (!url) {
      return 'Home';
    }
    // Implement logic to derive page name from URL
    const segments = url.split('/');
    return segments[segments.length - 1] || 'Home';
  }

  openProfile(event?: string): void {
    if (event === 'user-updated') {
      this.user = JSON.parse(localStorage.getItem('user'));
      this.showDropdown = false;
    } else if (event === EProfileState.PROFILE_COMPLETE) {
      this.showProfileFlyout = true;
      this.showDropdown = !this.showDropdown;
      localStorage.setItem(ELocalStorageKey.ProfileCompleted, 'true');
      localStorage.removeItem(ELocalStorageKey.ProfileOpened);
    } else {
      this.showProfileFlyout = true;
      this.showDropdown = !this.showDropdown;
    }
  }

  setProfileIntent(profileIntent: EProfileTab, event?: Event): void {
    if (!event) return;
    event.stopPropagation();
    if (this.showDropdown && this.openProfileIntent === profileIntent) return;
    this.openProfileIntent = profileIntent;
    this.openProfile();
  }

  getTokenType(tokenType: ETokenType): void {
    // To prevent ExpressionChanged error.
    setTimeout(() => {
      this.tokenType = tokenType;
    });
  }

  navigateToHome(): void {
    this.router.navigateByUrl(this.authService.$isUser.value ? '/' : '/?onOpen=signUp');
  }

  private startRouterListener(): void {
    if (!this.routerSub) {
      this.routerSub = this.router.events.subscribe(event => {
        if (event instanceof NavigationStart) {
          this.closeFlyout();
        }
      });
    }
  }

  private stopRouterListener(): void {
    this.routerSub?.unsubscribe();
    this.routerSub = undefined;
  }

  private closeFlyout(): void {
    this.showProfileFlyout = false;
    this.showDropdown = false;
    this.stopRouterListener();
  }

  ngOnDestroy(): void {
    this.tokensBalanceEvent$?.unsubscribe();
    this.tokensBalanceSubscription?.unsubscribe();
  }
}
